import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useToast } from '../../context/ToastContext';
import {
  TestVideo,
  ApplicationData,
  TestSubmission,
  VALID_VIDEO_TYPES,
  MAX_FILE_SIZE_BYTES,
  createEmptyVideo
} from './types';

export default function useTestUploadData() {
  const { token: urlToken } = useParams<{ token: string }>();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [applicationData, setApplicationData] = useState<ApplicationData | null>(null);
  const [existingSubmissions, setExistingSubmissions] = useState<TestSubmission[]>([]);

  const [videos, setVideos] = useState<TestVideo[]>([
    createEmptyVideo(1),
    createEmptyVideo(2)
  ]);

  useEffect(() => {
    if (urlToken) {
      validateToken(urlToken);
    } else {
      setError('Invalid upload link. Please check your email for the correct link.');
      setLoading(false);
    }
  }, [urlToken]);

  const validateToken = async (testToken: string) => {
    try {
      setLoading(true);

      const { data: application, error: appError } = await supabase
        .from('applications')
        .select('*')
        .eq('test_token', testToken)
        .single();

      if (appError || !application) {
        setError('Invalid or expired upload link. Please contact support if you believe this is an error.');
        setLoading(false);
        return;
      }

      if (application.test_link_expires_at) {
        const expiresAt = new Date(application.test_link_expires_at);
        if (expiresAt < new Date()) {
          setError('This upload link has expired. Please contact support to request a new link.');
          setLoading(false);
          return;
        }
      }

      setApplicationData(application as ApplicationData);

      const { data: submissions, error: subError } = await supabase
        .from('test_submissions')
        .select('*')
        .eq('application_id', application.id)
        .order('video_number');

      if (subError) {
        console.error('Error fetching submissions:', subError);
      } else if (submissions) {
        const typedSubmissions = submissions as TestSubmission[];
        setExistingSubmissions(typedSubmissions);

        const updatedVideos: TestVideo[] = [createEmptyVideo(1), createEmptyVideo(2)];
        typedSubmissions.forEach((sub) => {
          const idx = sub.video_number - 1;
          updatedVideos[idx] = {
            ...updatedVideos[idx],
            uploaded: true,
            uploadType: sub.upload_type,
            url: sub.video_url || '',
            title: sub.title || '',
            notes: sub.notes || ''
          };
        });
        setVideos(updatedVideos);
      }

      setLoading(false);
    } catch (err: unknown) {
      console.error('Error validating token:', err);
      setError('An error occurred while validating your link. Please try again.');
      setLoading(false);
    }
  };

  const handleUploadTypeChange = (videoIdx: number, type: 'file' | 'url') => {
    const updatedVideos = [...videos];
    updatedVideos[videoIdx] = {
      ...updatedVideos[videoIdx],
      uploadType: type,
      file: null,
      url: ''
    };
    setVideos(updatedVideos);
  };

  const handleFileChange = (videoIdx: number, file: File | null) => {
    if (!file) return;

    if (!VALID_VIDEO_TYPES.includes(file.type)) {
      showToast('Please upload a valid video file (MP4, MOV, AVI, MKV, WEBM)', 'error');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      showToast('File size exceeds 300 MB. Please compress your video or upload a smaller file.', 'error');
      return;
    }

    const updatedVideos = [...videos];
    updatedVideos[videoIdx] = { ...updatedVideos[videoIdx], file };
    setVideos(updatedVideos);
  };

  const handleUrlChange = (videoIdx: number, url: string) => {
    const updatedVideos = [...videos];
    updatedVideos[videoIdx] = { ...updatedVideos[videoIdx], url };
    setVideos(updatedVideos);
  };

  const handleTitleChange = (videoIdx: number, title: string) => {
    const updatedVideos = [...videos];
    updatedVideos[videoIdx] = { ...updatedVideos[videoIdx], title };
    setVideos(updatedVideos);
  };

  const handleNotesChange = (videoIdx: number, notes: string) => {
    const updatedVideos = [...videos];
    updatedVideos[videoIdx] = { ...updatedVideos[videoIdx], notes };
    setVideos(updatedVideos);
  };

  const uploadVideo = async (videoIdx: number) => {
    const video = videos[videoIdx];
    if (!applicationData) return;

    if (!video.uploadType) {
      showToast('Please select an upload method (File Upload or URL)', 'error');
      return;
    }

    if (video.uploadType === 'file' && !video.file) {
      showToast('Please select a video file to upload', 'error');
      return;
    }

    if (video.uploadType === 'url' && !video.url.trim()) {
      showToast('Please enter a video URL', 'error');
      return;
    }

    const updatedVideos = [...videos];
    updatedVideos[videoIdx] = { ...updatedVideos[videoIdx], uploading: true };
    setVideos(updatedVideos);

    try {
      let storagePath: string | null = null;
      let fileSizeBytes: number | null = null;

      if (video.uploadType === 'file' && video.file) {
        const fileExt = video.file.name.split('.').pop();
        const fileName = `test_${applicationData.id}_video${video.videoNumber}_${Date.now()}.${fileExt}`;
        const filePath = `test-videos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('videos')
          .upload(filePath, video.file);

        if (uploadError) {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }

        storagePath = filePath;
        fileSizeBytes = video.file.size;
      }

      const existingSubmission = existingSubmissions.find(
        (s) => s.video_number === video.videoNumber
      );

      if (existingSubmission) {
        if (existingSubmission.storage_path) {
          await supabase.storage
            .from('videos')
            .remove([existingSubmission.storage_path]);
        }

        const { error: updateError } = await supabase
          .from('test_submissions')
          .update({
            upload_type: video.uploadType,
            storage_path: storagePath,
            video_url: video.uploadType === 'url' ? video.url.trim() : null,
            title: video.title.trim() || `Test Video ${video.videoNumber}`,
            notes: video.notes.trim() || null,
            file_size_bytes: fileSizeBytes,
            screening_status: 'pending',
            screening_notes: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSubmission.id);

        if (updateError) {
          throw new Error(`Database error: ${updateError.message}`);
        }
      } else {
        const { error: insertError } = await supabase
          .from('test_submissions')
          .insert({
            application_id: applicationData.id,
            video_number: video.videoNumber,
            upload_type: video.uploadType,
            storage_path: storagePath,
            video_url: video.uploadType === 'url' ? video.url.trim() : null,
            title: video.title.trim() || `Test Video ${video.videoNumber}`,
            notes: video.notes.trim() || null,
            file_size_bytes: fileSizeBytes,
            screening_status: 'pending'
          });

        if (insertError) {
          throw new Error(`Database error: ${insertError.message}`);
        }
      }

      updatedVideos[videoIdx] = { ...updatedVideos[videoIdx], uploaded: true, uploading: false };
      setVideos(updatedVideos);

      showToast(
        existingSubmission
          ? `Video ${video.videoNumber} resubmitted successfully!`
          : `Video ${video.videoNumber} uploaded successfully!`,
        'success'
      );

      await supabase
        .from('applications')
        .update({ test_submitted: true })
        .eq('id', applicationData.id)
        .then(({ error: appUpdateError }) => {
          if (appUpdateError) console.error('Error updating application test_submitted:', appUpdateError);
        });

      const { data: submissions } = await supabase
        .from('test_submissions')
        .select('*')
        .eq('application_id', applicationData.id)
        .order('video_number');

      if (submissions) {
        setExistingSubmissions(submissions as TestSubmission[]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Upload error:', err);
      showToast(`Failed to upload video: ${message}`, 'error');
      updatedVideos[videoIdx] = { ...updatedVideos[videoIdx], uploading: false };
      setVideos(updatedVideos);
    }
  };

  const resubmitVideo = (videoIdx: number) => {
    const updatedVideos = [...videos];
    updatedVideos[videoIdx] = createEmptyVideo((videoIdx + 1) as 1 | 2);
    setVideos(updatedVideos);
  };

  const allVideosUploaded = videos.every(v => v.uploaded);

  return {
    loading,
    error,
    applicationData,
    existingSubmissions,
    videos,
    allVideosUploaded,
    handleUploadTypeChange,
    handleFileChange,
    handleUrlChange,
    handleTitleChange,
    handleNotesChange,
    uploadVideo,
    resubmitVideo
  };
}
